import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import {
  previewCorporateBatch,
  generateCorporateCampaign,
} from '../../api/index'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import { useCorporateGuard } from '../../hooks/useRoleGuard'
import styles from '../ImportMensuel/ImportMensuel.module.css'

const MONTHS = [
  { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' },
]

const isPdf = (f) => f?.name?.toLowerCase().endsWith('.pdf')

const CorporateImport = () => {
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useCorporateGuard()
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('profile')) } catch { return null }
  }, [])

  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [files, setFiles] = useState([])
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [search, setSearch] = useState('')
  const [showErrorsOnly, setShowErrorsOnly] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const [analyzeMode, setAnalyzeMode] = useState('auto')
  const dragCounter = useRef(0)
  const lastAnalyzedKeyRef = useRef('')
  const analyzeRequestIdRef = useRef(0)

  const periodLabel = `${MONTHS.find((m) => m.value === month)?.label} ${year}`
  const uploadedBy = user?.result?._id || 'corporate'

  useEffect(() => {
    if (!user) history.push('/')
    else if (user?.result?.role === 'user') history.push('/dashboard')
  }, [user, history])

  const buildFormData = () => {
    const formData = new FormData()
    files.forEach((f) => formData.append('files', f))
    formData.append('month', month)
    formData.append('year', year)
    formData.append('uploadedBy', uploadedBy)
    return formData
  }

  const applyFiles = (incoming) => {
    const list = Array.from(incoming || []).filter(isPdf)
    if (!list.length) {
      openSnackbar('Sélectionnez des fichiers PDF')
      return
    }
    setFiles(list)
    setPreview(null)
    lastAnalyzedKeyRef.current = ''
  }

  const runPreview = useCallback(async ({ force = false, showToast = false } = {}) => {
    if (!files.length) return null

    const analyzeKey = `${files.length}|${files.map((f) => `${f.name}-${f.size}`).join('|')}|${month}|${year}`
    if (!force && lastAnalyzedKeyRef.current === analyzeKey) {
      setLoading(false)
      return null
    }

    const requestId = ++analyzeRequestIdRef.current
    setLoading(true)

    try {
      const formData = new FormData()
      files.forEach((f) => formData.append('files', f))
      formData.append('month', month)
      formData.append('year', year)
      const { data } = await previewCorporateBatch(formData)
      if (requestId === analyzeRequestIdRef.current) {
        setPreview(data)
        lastAnalyzedKeyRef.current = analyzeKey
        if (showToast) {
          openSnackbarRef.current(`${data.validCount} PDF valide(s) sur ${data.totalRows}`)
        }
      }
      return data
    } catch (error) {
      if (requestId === analyzeRequestIdRef.current) {
        setPreview(null)
        lastAnalyzedKeyRef.current = ''
        openSnackbarRef.current(error?.response?.data?.message || 'Erreur analyse PDF')
      }
      return null
    } finally {
      if (requestId === analyzeRequestIdRef.current) {
        setLoading(false)
      }
    }
  }, [files, month, year])

  useEffect(() => {
    if (analyzeMode !== 'auto' || !files.length) {
      if (!files.length) {
        setPreview(null)
        lastAnalyzedKeyRef.current = ''
      }
      return undefined
    }

    runPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, month, year, analyzeMode])

  const handleManualPreview = async () => {
    if (!files.length) {
      openSnackbar('Sélectionnez des PDF')
      return
    }
    lastAnalyzedKeyRef.current = ''
    await runPreview({ force: true, showToast: true })
  }

  const handleAnalyzeModeChange = (mode) => {
    setAnalyzeMode(mode)
    if (mode === 'auto' && files.length) {
      lastAnalyzedKeyRef.current = ''
    }
    if (mode === 'manual') {
      setPreview(null)
      lastAnalyzedKeyRef.current = ''
    }
  }

  const filteredRows = useMemo(() => {
    if (!preview?.rows) return []
    const q = search.trim().toLowerCase()
    return preview.rows.filter((row) => {
      if (showErrorsOnly && row.valid) return false
      if (!q) return true
      const hay = [row.clientName, row.fileName, row.email, row.matriculeFiscal].join(' ').toLowerCase()
      return hay.includes(q)
    })
  }, [preview, search, showErrorsOnly])

  if (!user || user?.result?.role === 'user') return null

  const executeGenerate = async () => {
    setGenerating(true)
    try {
      const { data } = await generateCorporateCampaign(buildFormData())
      openSnackbar(data.message)
      setConfirmModal({
        type: 'sendEmails',
        variant: 'success',
        title: 'Campagne créée',
        message: 'Les PDF ont été enregistrés avec succès.',
        detail: 'Souhaitez-vous envoyer tous les emails aux clients maintenant ?',
        confirmLabel: 'Envoyer les emails',
        cancelLabel: 'Plus tard',
        campaignId: data.campaign._id,
      })
    } catch (error) {
      const status = error?.response?.status
      const data = error?.response?.data
      if (status === 409) {
        setConfirmModal({
          type: 'existingCampaign',
          variant: 'warning',
          title: 'Campagne déjà existante',
          message: data?.message || 'Une campagne existe déjà pour cette période.',
          detail: 'Consultez la campagne existante ou choisissez un autre mois.',
          confirmLabel: 'Voir la campagne',
          cancelLabel: 'Fermer',
          campaignId: data?.campaignId,
        })
        return
      }
      openSnackbar(data?.message || 'Erreur génération')
    } finally {
      setGenerating(false)
    }
  }

  const handleGenerate = () => {
    if (!files.length) {
      openSnackbar('Sélectionnez des PDF')
      return
    }
    if (loading) {
      openSnackbar('Analyse en cours…')
      return
    }
    if (!preview) {
      openSnackbar(analyzeMode === 'manual'
        ? 'Cliquez sur « Analyser les PDF » pour vérifier le matching'
        : 'Attendez la fin de l\'analyse')
      return
    }
    if (preview.hasDuplicates) {
      openSnackbar('Noms de fichiers en double — corrigez le lot')
      return
    }

    const blocked = preview.rows.filter((r) => !r.valid && !r.isNewClient)
    if (blocked.length > 0) {
      setConfirmModal({
        type: 'errors',
        variant: 'warning',
        title: 'Clients sans email',
        message: `${blocked.length} client(s) existent en base sans email.`,
        detail: 'Complétez les emails dans Clients convention, ou continuez — ces PDF seront enregistrés mais non envoyables.',
        confirmLabel: 'Continuer quand même',
        cancelLabel: 'Annuler',
      })
      return
    }

    if (preview.newClients > 0) {
      setConfirmModal({
        type: 'newClients',
        variant: 'info',
        title: 'Nouveaux clients',
        message: `${preview.newClients} nouveau(x) client(s) seront créés automatiquement.`,
        detail: 'Ajoutez leurs emails dans Clients convention avant l\'envoi des emails.',
        confirmLabel: 'Créer la campagne',
        cancelLabel: 'Annuler',
      })
      return
    }

    executeGenerate()
  }

  const handleConfirmModal = () => {
    if (!confirmModal) return
    if (['errors', 'newClients'].includes(confirmModal.type)) {
      setConfirmModal(null)
      executeGenerate()
      return
    }
    if (confirmModal.type === 'sendEmails') {
      const { campaignId } = confirmModal
      setConfirmModal(null)
      history.push(`/corporate/campagnes/${campaignId}?sendAll=1`)
      return
    }
    if (confirmModal.type === 'existingCampaign' && confirmModal.campaignId) {
      const { campaignId } = confirmModal
      setConfirmModal(null)
      history.push(`/corporate/campagnes/${campaignId}`)
    }
  }

  const handleCancelModal = () => {
    if (confirmModal?.type === 'sendEmails') {
      history.push(`/corporate/campagnes/${confirmModal.campaignId}`)
    }
    setConfirmModal(null)
  }

  const currentStep = !files.length ? 1 : (analyzeMode === 'manual' && !preview && !loading) ? 2 : !preview ? 2 : 3
  const previewButtonLabel = analyzeMode === 'manual' && !preview
    ? (loading ? 'Analyse en cours…' : 'Analyser les PDF')
    : (loading ? 'Analyse en cours…' : 'Réanalyser les PDF')

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => history.push('/corporate/dashboard')}>
        ← Retour Corporate
      </button>

      <div className={styles.topBar}>
        <div>
          <h1>Envoi PDF — Conventions</h1>
          <p>California Gym — Export Sage X3 → envoi clients Corporate</p>
        </div>
      </div>

      <div className={styles.periodBanner}>
        <div>
          <span className={styles.periodLabel}>Période de facturation</span>
          <strong>{periodLabel}</strong>
        </div>
        <div className={styles.periodStatus}>
          <span className={styles.badgeInfo}>Matching 100 % nom fichier ↔ client BD</span>
        </div>
      </div>

      <div className={styles.steps}>
        <div className={`${styles.step} ${currentStep >= 1 ? styles.stepActive : ''}`}>
          <span className={styles.stepNum}>1</span><span>PDF Sage X3</span>
        </div>
        <div className={styles.stepLine} />
        <div className={`${styles.step} ${currentStep >= 2 ? styles.stepActive : ''}`}>
          <span className={styles.stepNum}>2</span><span>Analyse & matching</span>
        </div>
        <div className={styles.stepLine} />
        <div className={`${styles.step} ${currentStep >= 3 ? styles.stepActive : ''}`}>
          <span className={styles.stepNum}>3</span><span>Campagne & envoi</span>
        </div>
      </div>

      <div className={styles.twoCol}>
        <div className={styles.panel}>
          <h2>Configuration</h2>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label htmlFor="corp-month">Mois</label>
              <select id="corp-month" className={styles.select} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label htmlFor="corp-year">Année</label>
              <select id="corp-year" className={styles.select} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.modeField}>
            <label>Vérification</label>
            <div className={styles.modeToggle} role="group" aria-label="Mode de vérification">
              <button
                type="button"
                className={`${styles.modeOption} ${analyzeMode === 'auto' ? styles.modeOptionActive : ''}`}
                onClick={() => handleAnalyzeModeChange('auto')}
              >
                <span className={styles.modeOptionTitle}>Automatique</span>
                <span className={styles.modeOptionHint}>Analyse dès l&apos;import ou changement de période</span>
              </button>
              <button
                type="button"
                className={`${styles.modeOption} ${analyzeMode === 'manual' ? styles.modeOptionActive : ''}`}
                onClick={() => handleAnalyzeModeChange('manual')}
              >
                <span className={styles.modeOptionTitle}>Manuelle</span>
                <span className={styles.modeOptionHint}>Vous lancez la vérification quand vous êtes prêt</span>
              </button>
            </div>
          </div>

          <div
            className={styles.uploadZone}
            onDragEnter={(e) => { e.preventDefault(); dragCounter.current += 1; setIsDragging(true) }}
            onDragLeave={(e) => { e.preventDefault(); dragCounter.current -= 1; if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false) } }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); dragCounter.current = 0; setIsDragging(false); applyFiles(e.dataTransfer.files) }}
          >
            <label htmlFor="corp-pdfs" className={`${styles.uploadLabel} ${isDragging ? styles.uploadDragActive : ''} ${files.length ? styles.uploadHasFile : ''}`}>
              <span className={styles.uploadIcon}>{isDragging ? '📥' : '📄'}</span>
              <strong>{isDragging ? 'Déposez les PDF ici' : files.length ? `${files.length} PDF sélectionné(s)` : 'Sélectionner les PDF Sage X3'}</strong>
              <span className={styles.uploadHint}>
                Nom du fichier = nom exact du client en base ·{' '}
                {analyzeMode === 'auto'
                  ? 'analyse automatique à l\'import'
                  : 'puis « Analyser les PDF »'}
              </span>
              <span className={styles.uploadBtn}>Parcourir</span>
            </label>
            <input id="corp-pdfs" type="file" accept=".pdf" multiple className={styles.fileInput} onChange={(e) => applyFiles(e.target.files)} />
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={handleManualPreview}
              disabled={loading || !files.length}
            >
              {previewButtonLabel}
            </button>
            <button type="button" className={styles.btnPrimary} onClick={handleGenerate} disabled={generating || loading || !files.length || !preview || preview?.hasDuplicates || preview?.validCount === 0}
              title={
                loading
                  ? 'Analyse en cours…'
                  : !preview && files.length
                    ? analyzeMode === 'manual'
                      ? 'Lancez d\'abord l\'analyse des PDF'
                      : 'Attendez la fin de l\'analyse automatique'
                    : undefined
              }
            >
              {generating ? 'Création…' : `Créer campagne (${preview?.validCount || 0} PDF)`}
            </button>
          </div>
        </div>

        <div className={styles.panel}>
          <h2>Règles de matching</h2>
          <ul className={styles.formatList}>
            <li><span>1</span> 1 PDF = 1 client convention</li>
            <li><span>2</span> Nom fichier (sans .pdf) = nom client BD</li>
            <li><span>3</span> Nouveau nom → client créé auto</li>
            <li><span>4</span> Email requis avant envoi</li>
          </ul>
          <div className={styles.noteBox}>
            <strong>Sage X3 :</strong> exportez les PDF avec le nom société exactement comme en base clients.
          </div>
        </div>
      </div>

      {files.length > 0 && analyzeMode === 'manual' && !preview && !loading && (
        <div className={styles.alertBox}>
          <strong>Vérification manuelle</strong> — cliquez sur « Analyser les PDF » pour lancer le matching.
        </div>
      )}

      {loading && files.length && !preview && (
        <div className={styles.alertBox}>
          Analyse des PDF en cours…
        </div>
      )}

      {preview && (
        <>
          <div className={styles.kpiGrid}>
            <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
              <span className={styles.kpiLabel}>PDF valides</span>
              <span className={styles.kpiValue}>{preview.validCount}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Nouveaux clients</span>
              <span className={styles.kpiValue}>{preview.newClients}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Erreurs</span>
              <span className={`${styles.kpiValue} ${preview.errorCount > 0 ? styles.kpiError : ''}`}>{preview.errorCount}</span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Total PDF</span>
              <span className={styles.kpiValue}>{preview.totalRows}</span>
            </div>
          </div>

          {preview.hasDuplicates && (
            <div className={styles.alertBox}>
              <strong>Génération bloquée</strong> — noms de fichiers en double dans le lot.
            </div>
          )}

          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2>Aperçu matching</h2>
              <span className={styles.pageInfo}>{filteredRows.length} ligne(s)</span>
            </div>
            <div className={styles.toolbar}>
              <input type="search" className={styles.searchInput} placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
              <label className={styles.checkFilter}>
                <input type="checkbox" checked={showErrorsOnly} onChange={(e) => setShowErrorsOnly(e.target.checked)} />
                Erreurs uniquement
              </label>
            </div>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fichier PDF</th>
                    <th>Client BD</th>
                    <th>MF</th>
                    <th>Email</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.rowIndex} className={row.valid ? '' : styles.errorRow}>
                      <td data-label="#">{row.rowIndex}</td>
                      <td data-label="Fichier PDF">{row.fileName}</td>
                      <td data-label="Client BD">{row.clientName}</td>
                      <td data-label="MF">{row.matriculeFiscal || '—'}</td>
                      <td data-label="Email">{row.email || '—'}</td>
                      <td data-label="Statut">
                        {row.valid ? (
                          <span className={styles.badgeOk}>{row.isNewClient ? 'Nouveau' : 'OK'}</span>
                        ) : (
                          <span className={styles.badgeErr} title={row.rowErrors?.join(', ')}>
                            {row.rowErrors?.[0] || 'Erreur'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        open={Boolean(confirmModal)}
        variant={confirmModal?.variant || 'info'}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        detail={confirmModal?.detail}
        confirmLabel={confirmModal?.confirmLabel || 'Confirmer'}
        cancelLabel={confirmModal?.cancelLabel || 'Annuler'}
        onConfirm={handleConfirmModal}
        onCancel={handleCancelModal}
        loading={generating}
      />
    </div>
  )
}

export default CorporateImport
